import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import type { SessionSummary as SessionSummaryData } from '@/api/review'

interface SessionSummaryProps {
  summary: SessionSummaryData
  onRestart: () => void
}

export function SessionSummary({ summary, onRestart }: SessionSummaryProps) {
  const navigate = useNavigate()

  return (
    <Card className="mx-auto w-full max-w-3xl" data-testid="session-summary">
      <CardHeader>
        <CardTitle>Session complete</CardTitle>
        <CardDescription>Great work. Here&apos;s how this review session went.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Accuracy" value={`${summary.accuracy.toFixed(0)}%`} />
          <Stat label="Cards reviewed" value={String(summary.cardsReviewed)} />
          <Stat label="Study time" value={`${summary.studyTimeMin} min`} />
          <Stat label="Current streak" value={`${summary.currentStreak} day${summary.currentStreak === 1 ? '' : 's'}`} />
        </div>

        {summary.streakUpdated ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            Your streak has been updated for today.
          </div>
        ) : null}

        {summary.growthHighlight ? (
          <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-foreground">
            {summary.growthHighlight}
          </div>
        ) : null}

        <div className="flex flex-col gap-3 sm:flex-row">
          <Button className="sm:w-auto" onClick={onRestart}>Review More</Button>
          <Button
            className="sm:w-auto bg-secondary text-secondary-foreground hover:bg-secondary/80"
            onClick={() => navigate('/vocabulary')}
          >
            Go to Vocabulary
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-2 text-2xl font-bold text-foreground">{value}</p>
    </div>
  )
}
